import { formatDelta, kindCode, kindLabel, isD9Kind } from './pivotCommonalities';

/**
 * The chart half of the Pivot Planetary Commonalities study: select a pair, see the two bars.
 *
 * <h2>TWO MARKS AND TWO VERTICALS, not twenty-five</h2>
 *
 * The analog scanner paints a whole ranked set at once, because its question is "where else has
 * this sky been". This one answers "WHICH TWO BARS is this row about", so it draws exactly the
 * selected pair -- and that is what lets the verticals be solid and legible where the analog's
 * had to be faint enough not to stripe the chart.
 *
 * <h2>⚠ THESE MARKS SIT AT A PRICE, and that is the opposite of the analog's rule</h2>
 *
 * A planetary analog has no price -- it is a date whose sky resembled another date's -- so its
 * marks go to the rail, where the absence of a price IS the instruction. A PIVOT is a price: it
 * is the extreme of its own window, the thing the whole study is measured between. Burying it at
 * the foot of the grid would throw away the one reading a chart can give it, which is whether
 * the two pivots the row names are the swing high and the swing low a reader can see.
 *
 * <p>So the mark is drawn at `pivot.price`, pointing away from the bar -- a triangle above a
 * HIGH and an inverted one below a LOW -- which means the shape alone says which kind it is in a
 * monochrome screenshot.
 */

export const PIVOT_COMMONALITY_OVERLAY_ID = 'pivot-commonality-pair';

/**
 * Amber, and NOT the analog scanner's pink.
 *
 * <p>Both studies can be on one chart. The pink is 4.17:1 on the light canvas and 4.39:1 on the
 * dark one; this amber measures 4.6:1 and 3.9:1 on the same two, so both clear the 3:1 floor for
 * a graphical object and the two studies are distinguishable by hue rather than by position.
 */
export const PIVOT_COMMONALITY_COLOR = '#B8860B';

/** Solid and visible: two lines, not a stripe pattern. */
export const VERTICAL_OPACITY = 0.55;

/** The most matches a hover card will print before saying how many it kept back. */
export const CARD_MAX_MATCHES = 8;

/**
 * The first index at or after a day.
 *
 * <p>⚠ RETURNS -1 BEFORE THE SERIES, never 0. The analog overlay shipped without that guard and
 * piled seven marks onto bar 0 when the lookback reached before the chart's first bar, reporting
 * them as collisions. A pivot is derived from the chart's own bars so it cannot predate them --
 * which makes this the guard for the day somebody loads a report against a different symbol.
 */
export function indexAtOrAfter(dates, day) {
  if (!Array.isArray(dates) || dates.length === 0 || !day) return -1;
  if (day < dates[0]) return -1;
  let lo = 0;
  let hi = dates.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (dates[mid] >= day) {
      found = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  return found;
}

const kindOf = (m) => (m && m.kind) || '';

/** `Mars` or `Saturn → Mars`, which is how the matrix names the same thing. */
export function matchBodies(match) {
  if (!match) return '';
  return match.from === match.to ? match.from : `${match.from} → ${match.to}`;
}

/** One line of a hover card: the code, the bodies, and the delta where there is one. */
export function matchLine(match) {
  const code = kindCode(kindOf(match));
  const bodies = matchBodies(match);
  const motion = match.motionHeld ? 'motion held' : 'motion flipped';
  if (Number.isFinite(match.deltaDeg)) {
    return `  ${code}  ${bodies}  ${formatDelta(match.deltaDeg)}  ·  ${motion}`;
  }
  // A D9 kind makes a claim about signs rather than degrees, so printing a delta of `—` would
  // imply it had one and failed to measure it.
  return `  ${code}  ${bodies}  ${match.detail || kindLabel(kindOf(match))}`;
}

/**
 * The hover card for a pivot mark.
 *
 * <p>IDENTICAL on both ends of the pair, deliberately: the card is about the PAIR, and a reader
 * who hovers whichever of the two is nearer their cursor should not have to find the other one
 * to read the same answer.
 */
export function pairCard(pair, role, match) {
  if (!pair || !pair.a || !pair.b) return [];
  const lines = [];
  lines.push(`PIVOT PAIR  ${pair.transitionLabel || pair.transition || ''}`.trim());
  // The SELECTED commonality first, where there is one: it is the reason this pair is on the
  // chart, and the full list below runs to fourteen rows on a real pair.
  if (match) lines.push(`SELECTED  ${kindLabel(match.kind)} — ${matchLine(match).trim()}`);
  lines.push('');
  lines.push(`A  ${pair.a.date}  ${pair.a.kind}  ${Number(pair.a.price).toFixed(2)}`
    + (role === 'A' ? '   ← this mark' : ''));
  lines.push(`B  ${pair.b.date}  ${pair.b.kind}  ${Number(pair.b.price).toFixed(2)}`
    + (role === 'B' ? '   ← this mark' : ''));
  lines.push(`span ${pair.barSpan} bars`);
  lines.push('');
  const matches = Array.isArray(pair.matches) ? pair.matches : [];
  const d9 = matches.filter((m) => isD9Kind(kindOf(m))).length;
  lines.push(`${pair.matchCount} commonalities`
    + (d9 ? `, ${d9} of them Navamsha` : '')
    + `  ·  ${Number(pair.bits).toFixed(1)} bits`);
  matches.slice(0, CARD_MAX_MATCHES).forEach((m) => lines.push(matchLine(m)));
  const hidden = matches.length - Math.min(matches.length, CARD_MAX_MATCHES)
    + (Number.isFinite(pair.matchesHidden) ? pair.matchesHidden : 0);
  if (hidden > 0) lines.push(`  …and ${hidden} more`);
  lines.push('');
  // ⚠ ON EVERY CARD. Bits are a measure of how unusual an agreement is in THIS run's own
  // history. Nothing here measures a forward return, and a reader meeting a ten-bit figure for
  // the first time will otherwise read it as a confidence.
  lines.push('Bits measure how UNUSUAL this agreement is across the scanned');
  lines.push('pairs — not a probability that anything follows the pivot.');
  return lines;
}

/**
 * The chip on a mark.
 *
 * <p>⚠ IT NAMES THE MATCH WHEN THERE IS ONE, because that is why the reader clicked. Selecting
 * `XFR Uranus → Neptune` from the matrix and getting two chips that say only "A · Low 125.40"
 * leaves the chart unable to say WHICH of the pair's seventy-two commonalities is being looked
 * at -- the canvas and the table would be answering different questions a centimetre apart.
 *
 * <p>Selecting a PAIR rather than a match carries no commonality to name, so the chip falls
 * back to the pivot alone rather than picking one of the seventy-two arbitrarily.
 */
export function badgeFor(pivot, role, match) {
  const kind = pivot.kind === 'HIGH' ? 'High' : 'Low';
  const base = `${role} · ${kind} ${Number(pivot.price).toFixed(2)}`;
  if (!match) return base;
  const delta = Number.isFinite(match.deltaDeg) ? ` ${formatDelta(match.deltaDeg)}` : '';
  return `${base} · ${kindCode(match.kind)} ${matchBodies(match)}${delta}`;
}

/**
 * Build the overlay for one selected pair.
 *
 * @param pair  one element of the report's `pairs`
 * @param dates the chart's own axis, which is what decides whether a date is drawable
 * @param match the specific commonality the reader selected, when they selected one
 */
export function buildPivotCommonalityOverlay(pair, dates, match = null) {
  const points = [];
  const verticals = [];
  const bars = [];
  let missing = 0;

  const place = (pivot, role) => {
    if (!pivot || !pivot.date) return;
    const index = indexAtOrAfter(dates, pivot.date);
    if (index === -1) {
      // DROPPED, never clamped. A mark at the wrong end of the chart is worse than an absent
      // one, and the caller reports the count rather than leaving the reader to wonder.
      missing += 1;
      return;
    }
    const day = dates[index];
    const high = pivot.kind === 'HIGH';
    bars.push(day);
    verticals.push({ date: day });
    points.push({
      date: day,
      price: pivot.price,
      // ABOVE a high, BELOW a low. The side is the reading: a mark under a swing high would
      // sit in the middle of the price action and say nothing about which extreme it names.
      position: high ? 'above' : 'below',
      symbol: 'triangle',
      // Rotated for a LOW, so the two are distinguishable with no colour at all.
      symbolRotate: high ? 0 : 180,
      offsetPx: 18,
      label: role,
      labelInside: true,
      badge: badgeFor(pivot, role, match),
      title: `Pivot ${role}`,
      tooltipLines: pairCard(pair, role, match),
    });
  };

  place(pair && pair.a, 'A');
  place(pair && pair.b, 'B');

  if (points.length === 0) return { overlay: null, bars: [], missing };

  return {
    overlay: {
      id: PIVOT_COMMONALITY_OVERLAY_ID,
      type: 'pivotMarkers',
      label: 'Pivot commonality pair',
      color: PIVOT_COMMONALITY_COLOR,
      symbolSize: 20,
      insideLabelFontSize: 11,
      badgeFontSize: 10,
      // ⚠ 34, NOT THE RENDERER'S DEFAULT OF 28. The badge series carries
      // `labelLayout: { hideOverlap: true }`, so at 28 the chip's rect touches this 20px
      // triangle's own 11px "A" and ECharts DROPS IT -- no warning and no element, so a study
      // that correctly asked for `badgeVisible` shows nothing at all. Found by screenshot:
      // every option field read correctly while the canvas had no chip on it.
      badgeOffsetPx: 34,
      // OPT-IN, because the badge label inherits the anchor's `opacity: 0` otherwise. Turning it
      // on unconditionally made the CTS confluence badges visible for the first time, on a study
      // nobody had asked to change.
      badgeVisible: true,
      verticals,
      verticalOpacity: VERTICAL_OPACITY,
      // Both gates. `researchTooltip` exempts an admin study from the short-bar lock;
      // `statsTooltip` exempts it from the reader's OHLCV-floater preference, which defaults ON
      // and would otherwise silence the card for everybody. Neither is sufficient without the
      // series id being in TOOLTIP_ALLOWED_PREFIXES, which runs last and strips the rest.
      researchTooltip: true,
      statsTooltip: true,
      confineTooltip: true,
      points,
    },
    bars,
    missing,
  };
}

/** What the status line says after a paint. */
export function describePivotCommonalityPaint({ pair, match, bars, missing }) {
  if (!pair) return 'Select a pair or a match to mark it on the chart.';
  if (!bars || bars.length === 0) {
    return `Neither pivot of ${pair.a.date} → ${pair.b.date} is on this chart — `
      + 'the report was run against a different symbol or range.';
  }
  const parts = [`Marked ${pair.a.date} (${pair.a.kind}) and ${pair.b.date} (${pair.b.kind})`];
  parts.push(`${pair.barSpan} bars apart`);
  // The SELECTED commonality rather than the total, where there is one: the reader clicked a
  // row, and "72 commonalities" does not tell them which of the 72 they are now looking at.
  if (match) {
    parts.push(`${kindCode(match.kind)} ${matchBodies(match)}`
      + (Number.isFinite(match.deltaDeg) ? ` ${formatDelta(match.deltaDeg)}` : '')
      + ` of ${pair.matchCount}`);
  } else {
    parts.push(`${pair.matchCount} commonalities`);
  }
  if (missing) parts.push(`${missing} pivot not on this chart`);
  return `${parts.join(' · ')}.`;
}
